import { Navigate } from 'react-router-dom'
import { KitDeviceScreen } from '../kit/KitDeviceScreen'
import { TabletConfirmScreen } from './TabletConfirmScreen'
import { TabletConsentScreen } from './TabletConsentScreen'
import { TabletPinScreen } from './TabletPinScreen'
import { TabletDoneScreen, useTabletContext } from './TabletLayout'

/**
 * Índice de `/tablet`: elige la pantalla según la fase de la sesión de tablet
 * que lleva `TabletLayout`. Es un switch y nada más — el estado, los timers y el
 * logout viven en el layout, así que volver al PIN entre jugadores no
 * desmonta ni recarga nada.
 */
export function TabletEntry() {
  const { phase, session, endSession, setAccess, bindingReason } = useTabletContext()

  switch (phase) {
    case 'code':
      // Paso 1: la tablet se autentica con el club. Mismo formulario que usa
      // `/login` y espejo del visor — sólo cambia cómo se nombra el
      // dispositivo (`kit/deviceCopy.ts`).
      return <KitDeviceScreen device="tablet" onVerified={setAccess} reason={bindingReason} />
    case 'pin':
      return <TabletPinScreen />
    case 'confirm':
      return <TabletConfirmScreen />
    case 'consent':
      return <TabletConsentScreen />
    case 'profile':
      return <Navigate to="/tablet/profile" replace />
    case 'checkin':
      return <Navigate to="/tablet/checkin" replace />
    case 'uptodate':
      return (
        <UpToDateScreen displayName={session?.displayName ?? null} onDone={endSession} />
      )
    case 'questionnaire': {
      // Sólo se llega acá con el "atrás" del navegador durante el
      // cuestionario: volvemos a la ruta hija en vez de dejar la tablet en
      // una pantalla vacía.
      const pending = session?.gates.questionnaire_pending
      return pending ? (
        <Navigate to={`/tablet/data/${encodeURIComponent(pending.code)}`} replace />
      ) : (
        <TabletPinScreen />
      )
    }
    case 'thanks':
      // La renderiza `TabletLayout` por encima del Outlet.
      return null
  }
}

/**
 * Nada pendiente: el jugador va derecho al visor. Muestra la MISMA guía de
 * cierre que el post-cuestionario (`TabletDoneScreen`) — los dos caminos
 * terminan igual, poniéndose los sensores — y sólo cambia el encabezado, que
 * acá no tiene respuestas que acusar. Se cierra con el botón "Listo" al
 * final de la guía; no tiene reloj propio.
 *
 * El título era "Estás al día" y el cuerpo terminaba en "ya podés usar el
 * visor" + "Volvemos a la pantalla de PIN en N s": mg (2026-09-07) reportó
 * que no se entendía — ni qué hacer ahora ni de qué dispositivo habla ese
 * "volvemos". La copia nueva nombra las dos cosas.
 */
function UpToDateScreen({
  displayName,
  onDone,
}: {
  displayName: string | null
  onDone: () => void
}) {
  const first = displayName?.split(' ')[0] ?? null
  return (
    <TabletDoneScreen
      title={first ? `Listo, ${first}` : 'Listo'}
      onDone={onDone}
    />
  )
}
